import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Loader2, Send, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useProfile } from "../../contexts/ProfileContext";
import { HOME_LINKS } from "../../config/homeLinks";

const getSupabaseUrl = () => {
  return (import.meta as any).env?.VITE_SUPABASE_URL || 'https://bandarmolony.supabase.co';
};
const logoUrl = `${getSupabaseUrl()}/storage/v1/object/public/assets/images/logo_bandarmolony_v2.png`;

export function Homepage() {
  const navigate = useNavigate();
  const { profile, isLoading } = useProfile();
  const [scale, setScale] = useState(1);
  const [slide, setSlide] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const SLIDES = 2;

  useEffect(() => {
    const updateScale = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const REF_W = 1280;
      const REF_H = 720;
      const sx = rect.width / REF_W;
      const sy = rect.height / REF_H;
      setScale(Math.min(sx, sy));
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="ml-2 text-muted-foreground">Loading…</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full w-full overflow-hidden">
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: `${100 / scale}%`,
          height: `${100 / scale}%`,
        }}
      >
        <div className="flex flex-col w-full h-full px-1">
          <div className="flex flex-col gap-1 h-full">

            {/* Video Section */}
            <Card className="overflow-hidden flex-[1.1] min-h-0 flex flex-col rounded-2xl border border-[#2a2a4a] gap-0 mt-1">
              <CardContent className="flex-1 p-2 min-h-0 [&:last-child]:pb-2">
                <div className="relative w-full h-full overflow-hidden bg-black flex items-center justify-center rounded-xl">
                  {slide === 0 ? (
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-contain scale-110"
                    >
                      <source src="/assets/videos/headline.mp4" type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      src="/assets/images/home-2.avif"
                      alt="Bandarmolony"
                      className="w-full h-full object-contain"
                      draggable={false}
                    />
                  )}
                  {/* Arrows */}
                  <button
                    onClick={() => setSlide((slide - 1 + SLIDES) % SLIDES)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors z-10"
                    aria-label="Sebelumnya"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setSlide((slide + 1) % SLIDES)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors z-10"
                    aria-label="Berikutnya"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  {/* Dots */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                    {[0, 1].map((i) => (
                      <button
                        key={i}
                        onClick={() => setSlide(i)}
                        className={`h-1.5 rounded-full transition-all ${slide === i ? "w-5 bg-white" : "w-1.5 bg-white/40"}`}
                        aria-label={`Slide ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Community Section */}
            <Card className="overflow-hidden flex-[0.85] min-h-0 flex flex-col rounded-2xl mb-1">
              <div className="flex flex-col sm:flex-row items-stretch h-full">
                {/* Header — left, icon above text */}
                <div className="flex flex-col sm:w-48 flex-shrink-0 items-center justify-center gap-1 p-3">
                  <img src="/assets/images/icon_comunity.png" alt="Community" className="h-20 w-20 flex-shrink-0" />
                  <div className="text-center">
                    <h2 className="text-lg font-bold leading-tight flex items-center justify-center gap-0.5">
                      Bandarmolo
                      <img src={logoUrl} alt="NY" className="h-5 inline-block" draggable={false} />
                    </h2>
                    <p className="text-sm text-muted-foreground">Communities</p>
                  </div>
                </div>

                {/* Cards — right */}
                <div className="min-w-0 p-2 overflow-hidden flex items-center justify-center flex-1">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full">
                  {/* Telegram */}
                  <Card className="flex flex-col overflow-hidden border-t-4 border-t-[#0088cc] hover:shadow-md transition-shadow">
                    <CardHeader className="text-center pb-1 pt-2 flex-1 flex flex-col items-center">
                      <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-[#0088cc]">
                        <Send className="h-8 w-8 text-white" />
                      </div>
                      <CardTitle className="text-base font-bold">Join Free Telegram Group</CardTitle>
                      <p className="text-xs font-medium mt-0.5">Bandarmolony Forum</p>
                      <p className="text-xs leading-snug mt-6">Sarana diskusi dan bertanya seputar platfrom Bandarmolony</p>
                    </CardHeader>
                    <CardContent className="px-4 pb-3 pt-0">
                      <Button size="default" className="w-full bg-[#0088cc] hover:bg-[#0088cc]/80 text-white h-10" onClick={() => window.open(HOME_LINKS.telegramGroup, "_blank", "noopener,noreferrer")}>
                        Join Sekarang <ArrowUpRight className="h-4 w-4 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Trading Group */}
                  <Card className="relative flex flex-col overflow-hidden border-t-4 border-t-[#c28830] hover:shadow-md transition-shadow">
                    <CardHeader className="text-center pb-1 pt-2 flex-1 flex flex-col items-center">
                      <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-[#c28830]/15 overflow-hidden">
                        <img src="/assets/images/logo_profile.jpeg" alt="Trading" className="h-16 w-16 object-cover" />
                      </div>
                      <CardTitle className="text-base font-bold">Join Free Trading Group</CardTitle>
                      <p className="text-xs leading-snug mt-0.5">Trading bareng dengan rekomendasi saham-saham menarik secara teknikal & bandarmology</p>
                      <p className="text-xs font-medium mt-1.5">Buka Akun di Sekuritas Partner</p>
                      <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                        Min. Deposit : Chat admin
                        <button
                          onClick={() => window.open("https://t.me/Nic_Yansen", "_blank", "noopener,noreferrer")}
                          className="flex h-5 w-5 items-center justify-center rounded-full bg-[#0088cc] hover:bg-[#0088cc]/80 transition-colors"
                          aria-label="Chat admin via Telegram"
                        >
                          <Send className="h-3 w-3 text-white" />
                        </button>
                      </p>
                    </CardHeader>
                    <CardContent className="px-3 pb-3 pt-0">
                      <div className="grid grid-cols-2 gap-2">
                        <Button size="default" className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold h-10" onClick={() => window.open(HOME_LINKS.valburyOpenAccount, "_blank", "noopener,noreferrer")}>
                          <span className="font-bold tracking-tight" style={{ fontFamily: 'Arial, sans-serif', color: '#3d2e00' }}>KB Valbury</span>
                          <ArrowUpRight className="h-4 w-4 ml-1" />
                        </Button>
                        <Button size="default" className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold h-10" onClick={() => window.open(HOME_LINKS.sucorOpenAccount, "_blank", "noopener,noreferrer")}>
                          <span className="font-bold tracking-tight" style={{ fontFamily: 'Arial, sans-serif' }}>Sucor</span>
                          <ArrowUpRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Bootcamp */}
                  <Card className="relative flex flex-col overflow-hidden border border-[#3a2e22] border-t-4 border-t-red-600 bg-[#1d1710] hover:shadow-md transition-shadow">
                    <div className="absolute right-[-55px] top-[32px] w-[250px] rotate-45 bg-red-600 py-2.5 text-center text-base font-bold text-white tracking-widest z-10 flex items-center justify-center whitespace-nowrap shadow-[0_0_12px_rgba(220,38,38,0.5)]">
                      OPEN NOW
                    </div>
                    <CardHeader className="text-center pb-1 pt-2 flex-1 flex flex-col items-center">
                      <div className="mb-2 flex h-16 w-16 items-center justify-center rounded-full overflow-hidden">
                        <img src="/assets/images/laptop_profile.jpeg" alt="Bootcamp" className="h-20 w-20 object-cover rounded-full" />
                      </div>
                      <CardTitle className="text-base font-bold">Join Trading Bootcamp</CardTitle>
                      <p className="text-xs leading-relaxed mt-2">Upgrade skill tradingmu dengan tools bandarmolony AI<br/>dan<br/>Praktek langsung trading bareng selama setahun penuh</p>
                    </CardHeader>
                    <CardContent className="px-3 pb-2 pt-0">
                      <Button className="w-full bg-red-600 hover:bg-red-700 text-white h-10" size="default" onClick={() => window.open("https://bandarmolony.com/bootcamp", "_blank", "noopener,noreferrer")}>
                        Lihat Kelas & Daftar <ArrowUpRight className="h-4 w-4 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                   </div>
                </div>
              </div>
            </Card>

          </div>
        </div>
      </div>
    </div>
  );
}
